import { useEffect, useState } from 'react';
import ChapterResourceList from '../components/ChapterResourceList';
import ResourceViewer from '../components/ResourceViewer';
import { ErrorBanner, InfoBanner } from '../components/Feedback';
import { api } from '../api/client';

/**
 * Module 8 — the Repository Browser: Subject → Class → Book → Chapter, then the
 * resources of the chapter.
 *
 * <p>The tree is exactly what the server sent. A teacher who is not yet verified, or
 * whose school has adopted nothing, gets an empty tree rather than a filtered view of
 * a fuller one — the filtering happens before the response is built.
 */
export default function RepositoryPage() {
  const [tree, setTree] = useState(null);
  const [open, setOpen] = useState({});
  const [book, setBook] = useState(null);
  const [chapters, setChapters] = useState(null);
  const [chapter, setChapter] = useState(null);
  const [resources, setResources] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.teacherTree().then(setTree).catch(setError);
  }, []);

  async function selectBook(node) {
    setBook(node);
    setChapter(null);
    setResources(null);
    setChapters(null);
    try {
      setChapters(await api.teacherChapters(node.titleId));
    } catch (err) {
      setError(err);
    }
  }

  async function selectChapter(summary) {
    setChapter(summary);
    setResources(null);
    try {
      setResources((await api.teacherResources(summary.chapterId)).resources);
    } catch (err) {
      setError(err);
    }
  }

  const toggle = (key) => setOpen((current) => ({ ...current, [key]: !current[key] }));

  return (
    <>
      <div className="page-head">
        <h1>Resource Repository</h1>
        <p>
          Teaching resources for the books your school has adopted. Everything opens in
          the viewer; nothing is stored on your device unless a resource allows it.
        </p>
      </div>

      <ErrorBanner error={error} onDismiss={() => setError(null)} />

      {tree !== null && tree.bookCount === 0 && (
        <InfoBanner>
          No books yet. Resources appear here once your account is verified and your
          school has adopted a title.
        </InfoBanner>
      )}

      {tree === null && <div className="empty">Loading…</div>}

      {tree !== null && tree.bookCount > 0 && (
        <div className="repo">
          <section className="card card-pad">
            <ul className="tree">
              {tree.subjects.map((subject) => {
                const subjectKey = `s:${subject.subject}`;
                const subjectOpen = open[subjectKey] !== false;
                return (
                  <li key={subjectKey} className="tree-group tree-subject">
                    <button type="button" onClick={() => toggle(subjectKey)}>
                      <span className={`tree-caret${subjectOpen ? ' open' : ''}`}>›</span>
                      {subject.subject}
                    </button>
                    {subjectOpen && (
                      <ul>
                        {subject.classes.map((level) => {
                          const classKey = `${subjectKey}/c:${level.classLevel}`;
                          const classOpen = open[classKey] !== false;
                          return (
                            <li key={classKey} className="tree-group">
                              <button type="button" onClick={() => toggle(classKey)}>
                                <span className={`tree-caret${classOpen ? ' open' : ''}`}>›</span>
                                Class {level.classLevel}
                              </button>
                              {classOpen && (
                                <ul>
                                  {level.books.map((node) => (
                                    <li key={node.titleId}>
                                      <button
                                        type="button"
                                        className={`tree-book${
                                          book?.titleId === node.titleId ? ' active' : ''
                                        }`}
                                        onClick={() => selectBook(node)}
                                      >
                                        <span style={{ flex: 1 }}>{node.name}</span>
                                        <span className="tiny muted">{node.chapterCount}</span>
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>

          <div style={{ display: 'grid', gap: 'var(--s1)' }}>
            {!book && <section className="card"><div className="empty">Choose a book.</div></section>}

            {book && (
              <section className="card">
                <div className="toolbar" style={{ padding: 'var(--s1) var(--s2)' }}>
                  <b>{book.name}</b>
                  <span className="muted small">{chapters?.chapters.length ?? 0} chapter(s)</span>
                </div>
                {chapters === null && <div className="empty">Loading…</div>}
                {chapters?.chapters.length === 0 && (
                  <div className="empty">No chapters have been published for this book yet.</div>
                )}
                {chapters?.chapters.map((summary) => (
                  <button
                    type="button"
                    key={summary.chapterId}
                    className={`chapter-row${
                      chapter?.chapterId === summary.chapterId ? ' active' : ''
                    }`}
                    onClick={() => selectChapter(summary)}
                  >
                    <span>
                      <span className="muted small">{summary.sequence}.</span> {summary.name}
                    </span>
                    <span className="tiny muted">{summary.resourceCount} resource(s)</span>
                  </button>
                ))}
              </section>
            )}

            {chapter && (
              <section className="card">
                <div className="toolbar" style={{ padding: 'var(--s1) var(--s2)' }}>
                  <b>{chapter.name}</b>
                </div>
                <ChapterResourceList
                  resources={resources}
                  loading={resources === null}
                  onOpen={setViewing}
                  emptyMessage="No resources for you in this chapter."
                />
              </section>
            )}
          </div>
        </div>
      )}

      {viewing && (
        <ResourceViewer resource={viewing} role="TEACHER" onClose={() => setViewing(null)} />
      )}
    </>
  );
}
